import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ArrowLeftIcon, PlusIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useLocation } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePonds, useMutation } from '../../hooks/useApi';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient from '../../utils/api';

interface IncomeFormData {
  pond_id?: string; // Optional for goat farming
  shed_id?: string; // For goat farming
  business_type?: string; // Auto-populated
  sale_date: string;
  fish_species?: string;
  amount_earned: number;
  quantity_sold_kg?: number;
  fish_count?: number;
  goat_ids?: string[]; // For goat farming - list of goat IDs being sold
  buyer_name?: string;
  buyer_contact?: string;
  remarks?: string;
}

// Dynamic schema based on business type
const getIncomeSchema = (businessType: 'fish' | 'goat') => {
  if (businessType === 'fish') {
    return yup.object({
      pond_id: yup.string().required('Please select a pond'),
      sale_date: yup.string().required('Sale date is required'),
      amount_earned: yup
        .number()
        .typeError('Amount must be a number')
        .transform((value, originalValue) => originalValue === '' ? undefined : value)
        .required('Amount earned is required')
        .positive('Amount must be positive'),
      quantity_sold_kg: yup
        .number()
        .typeError('Quantity must be a number')
        .transform((value, originalValue) => originalValue === '' ? undefined : value)
        .required('Quantity sold is required')
        .positive('Quantity must be positive'),
      fish_count: yup
        .number()
        .integer('Must be a whole number')
        .min(0, 'Count cannot be negative')
        .nullable()
        .transform((value, originalValue) => originalValue === '' ? undefined : value)
        .optional(),
      buyer_name: yup.string().optional(),
      buyer_contact: yup.string().optional(),
      remarks: yup.string().optional(),
    });
  } else {
    return yup.object({
      shed_id: yup.string().optional(),
      sale_date: yup.string().required('Sale date is required'),
      amount_earned: yup
        .number()
        .typeError('Amount must be a number')
        .transform((value, originalValue) => originalValue === '' ? undefined : value)
        .required('Amount earned is required')
        .positive('Amount must be positive'),
      quantity_sold_kg: yup
        .number()
        .typeError('Quantity must be a number')
        .transform((value, originalValue) => originalValue === '' ? undefined : value)
        .min(0, 'Quantity cannot be negative')
        .optional(),
      goat_ids: yup.array().of(yup.string()).optional(),
      buyer_name: yup.string().optional(),
      buyer_contact: yup.string().optional(),
      remarks: yup.string().optional(),
    });
  }
};

interface IncomeLog {
  id: string;
  pond_id: string;
  sale_date: string;
  fish_species?: string;
  amount_earned: number;
  quantity_sold_kg: number;
  fish_count?: number;
  avg_weight_g?: number;
  buyer_name?: string;
  buyer_contact?: string;
  remarks?: string;
  created_at: string;
  created_by: string;
}

const IncomeForm: React.FC = () => {
  const { businessType } = useBusinessTypeContext();
  const [sheds, setSheds] = useState<any[]>([]);
  const [goats, setGoats] = useState<any[]>([]);
  const [loadingGoats, setLoadingGoats] = useState(false);
  const { selectedLocation } = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [incomeLogs, setIncomeLogs] = useState<IncomeLog[]>([]);
  const [yearSummary, setYearSummary] = useState<{ year: number; total_income: number; total_sold_kg: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [speciesInventory, setSpeciesInventory] = useState<Array<{species: string; count: number; biomass_kg: number; avg_weight_g: number}>>([]);
  const [loadingSpecies, setLoadingSpecies] = useState(false);
  const [allFishSpecies, setAllFishSpecies] = useState<Array<{id: string; name: string}>>([]);
  const [loadingAllSpecies, setLoadingAllSpecies] = useState(false);
  const [editingLog, setEditingLog] = useState<IncomeLog | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<IncomeFormData>>({});

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const { mutate: createIncomeLog, loading: creating } = useMutation<IncomeLog, IncomeFormData>();
  const { mutate: updateIncomeLog, loading: updating } = useMutation<IncomeLog, Partial<IncomeFormData>>();

  // Load goat sheds and goats if business type is goat
  useEffect(() => {
    if (businessType === 'goat' && selectedLocation?.id) {
      loadGoatSheds();
      loadGoats();
    }
  }, [businessType, selectedLocation?.id]);

  const loadGoatSheds = async () => {
    try {
      const response = await apiClient.get(`/goat/sheds?location_id=${selectedLocation?.id}`);
      setSheds(response.data || []);
    } catch (err) {
      console.error('Failed to load goat sheds:', err);
    }
  };

  const loadGoats = async () => {
    try {
      setLoadingGoats(true);
      const response = await apiClient.get(`/goat/master?location_id=${selectedLocation?.id}&status=active`);
      setGoats(response.data || []);
    } catch (err) {
      console.error('Failed to load goats:', err);
      setGoats([]);
    } finally {
      setLoadingGoats(false);
    }
  };

  const form = useForm<IncomeFormData>({
    resolver: yupResolver(getIncomeSchema(businessType) as any) as any,
    defaultValues: {
      pond_id: '',
      sale_date: new Date().toISOString().split('T')[0],
      fish_species: undefined,
      amount_earned: undefined,
      quantity_sold_kg: undefined,
      fish_count: undefined,
      goat_ids: [],
      shed_id: '',
      buyer_name: '',
      buyer_contact: '',
      remarks: '',
    }
  });

  const watchedAmount = form.watch('amount_earned');
  const watchedQuantity = form.watch('quantity_sold_kg');
  const watchedWeight = form.watch('quantity_sold_kg');
  const watchedCount = form.watch('fish_count');
  const watchedPondId = form.watch('pond_id');
  const watchedSpecies = form.watch('fish_species');
  const watchedGoatIds = form.watch('goat_ids');
  const watchedGoatId = watchedGoatIds && watchedGoatIds.length > 0 ? watchedGoatIds[0] : '';

  useEffect(() => {
    if (selectedLocation?.id) {
      loadIncomeLogs();
    }
  }, [selectedLocation?.id]);

  useEffect(() => {
    const loadAllFishSpecies = async () => {
      if (!selectedLocation?.id) {
        setAllFishSpecies([]);
        return;
      }
      try {
        setLoadingAllSpecies(true);
        const response = await apiClient.get('/master-data/fish_species', {
          params: { location_id: selectedLocation.id }
        });
        setAllFishSpecies(response.data || []);
      } catch (err: any) {
        console.error('Error loading fish species:', err);
        setAllFishSpecies([]);
      } finally {
        setLoadingAllSpecies(false);
      }
    };
    loadAllFishSpecies();
  }, [selectedLocation?.id]);

  useEffect(() => {
    if (watchedPondId) {
      loadSpeciesInventory(watchedPondId);
    } else {
      setSpeciesInventory([]);
      form.setValue('fish_species', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedPondId]);

  const loadIncomeLogs = async () => {
    try {
      setLoading(true);
      // Use goat-specific endpoint if business type is goat
      const endpoint = businessType === 'goat' 
        ? `/goat/income-logs?location_id=${selectedLocation?.id}`
        : `/locations/${selectedLocation?.id}/income-logs`;
      const response = await apiClient.get(endpoint);
      setIncomeLogs(response.data.logs ?? response.data);
      if (response.data.current_year_summary) {
        setYearSummary(response.data.current_year_summary);
      } else {
        setYearSummary(null);
      }
    } catch (err: any) {
      setError('Failed to load income logs');
    } finally {
      setLoading(false);
    }
  };

  const loadSpeciesInventory = async (pondId: string) => {
    try {
      setLoadingSpecies(true);
      const response = await apiClient.get(`/ponds/${pondId}/species-inventory`);
      const inventory = response.data || [];
      setSpeciesInventory(inventory);
      // Auto-select species if only one is available
      if (inventory.length === 1) {
        form.setValue('fish_species', inventory[0].species);
      } else {
        form.setValue('fish_species', undefined);
      }
      console.log('Loaded species inventory:', inventory);
    } catch (err: any) {
      console.error('Error loading species inventory:', err);
      setSpeciesInventory([]);
      form.setValue('fish_species', undefined);
    } finally {
      setLoadingSpecies(false);
    }
  };

  const handleSubmit = async (data: IncomeFormData) => {
    try {
      setError(''); // Clear any previous errors
      
      // Ensure goat_ids is always an array if it exists
      let goatIdsArray: string[] | undefined = undefined;
      if (businessType === 'goat' && data.goat_ids) {
        // Handle both array and string cases (defensive programming)
        if (Array.isArray(data.goat_ids)) {
          goatIdsArray = data.goat_ids.length > 0 ? data.goat_ids : undefined;
        } else if (typeof data.goat_ids === 'string' && data.goat_ids) {
          goatIdsArray = [data.goat_ids];
        }
      }
      
      const incomeData = {
        ...data,
        location_id: selectedLocation?.id,
        // quantity_sold_kg is now required and validated to be > 0 for fish
        quantity_sold_kg: businessType === 'fish' ? data.quantity_sold_kg : (data.quantity_sold_kg ?? 0),
        // Clear pond_id if goat, clear shed_id if fish
        pond_id: businessType === 'fish' ? data.pond_id : undefined,
        shed_id: businessType === 'goat' ? data.shed_id : undefined,
        // For goat: send goat_ids as array, remove fish_count
        goat_ids: goatIdsArray,
        // Remove fish-specific fields for goat
        fish_species: businessType === 'fish' ? data.fish_species : undefined,
        fish_count: businessType === 'fish' ? data.fish_count : undefined,
      };
      // Use goat-specific endpoint if business type is goat
      const endpoint = businessType === 'goat' ? '/goat/income-logs' : '/income-logs';
      const newLog = await createIncomeLog(endpoint, incomeData);
      if (newLog) {
        // Reload income logs to get the latest data from backend
        await loadIncomeLogs();
        form.reset({
          pond_id: '',
          sale_date: new Date().toISOString().split('T')[0],
          fish_species: undefined,
          amount_earned: undefined,
          quantity_sold_kg: undefined,
          fish_count: undefined,
          goat_ids: [],
          shed_id: '',
          buyer_name: '',
          buyer_contact: '',
          remarks: '',
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to record sale');
      // Refresh income logs even on error, in case data was partially saved
      await loadIncomeLogs();
      // Reload species inventory in case it changed
      if (data.pond_id) {
        await loadSpeciesInventory(data.pond_id);
      }
    }
  };

  const handleEdit = (log: IncomeLog) => {
    setEditingLog(log);
    // Parse and normalize date for the date input (needs YYYY-MM-DD format)
    const parsedDate = parseDateString(log.sale_date);
    const saleDateForInput = parsedDate 
      ? `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, '0')}-${String(parsedDate.getDate()).padStart(2, '0')}`
      : (log.sale_date.includes('T') ? log.sale_date.split('T')[0] : log.sale_date);
    
    setEditFormData({
      pond_id: log.pond_id,
      sale_date: saleDateForInput,
      fish_species: log.fish_species || undefined,
      amount_earned: log.amount_earned,
      quantity_sold_kg: log.quantity_sold_kg,
      fish_count: log.fish_count,
      buyer_name: log.buyer_name || '',
      buyer_contact: log.buyer_contact || '',
      remarks: log.remarks || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingLog(null);
    setEditFormData({});
  };

  const handleUpdateSubmit = async () => {
    if (!editingLog) return;
    
    try {
      setError('');
      const updatedLog = await updateIncomeLog(`/income-logs/${editingLog.id}`, editFormData, 'PUT');
      if (updatedLog) {
        await loadIncomeLogs();
        handleCancelEdit();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update sale');
    }
  };

  const getPondName = (pondId: string) => {
    const pond = ponds?.find(p => p.id === pondId);
    return pond ? `${pond.name} (${pond.pond_type})` : 'Unknown Pond';
  };

  const calculatePricePerKg = () => {
    if (watchedAmount && watchedQuantity && watchedQuantity > 0) {
      return (watchedAmount / watchedQuantity).toFixed(2);
    }
    return null;
  };

  const calculateAvgWeight = () => {
    if (watchedWeight && watchedCount && watchedCount > 0) {
      return ((watchedWeight * 1000) / watchedCount).toFixed(1);
    }
    return null;
  };

  const getTotalIncome = () => {
    if (yearSummary) return yearSummary.total_income;
    return incomeLogs.reduce((total, log) => total + log.amount_earned, 0);
  };

  const getTotalQuantitySold = () => {
    if (yearSummary) return yearSummary.total_sold_kg;
    return incomeLogs.reduce((total, log) => total + log.quantity_sold_kg, 0);
  };

  // Parse date string to Date object, handling multiple formats
  // Prioritizes DD/MM/YYYY format (common in India)
  const parseDateString = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    
    // Remove time part if present
    const dateOnly = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    
    // Check if it's already in ISO format (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      return new Date(dateOnly + 'T00:00:00');
    }
    
    // Try DD/MM/YYYY format (common in India)
    const slashMatch = dateOnly.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const [, part1, part2, year] = slashMatch;
      const num1 = parseInt(part1);
      const num2 = parseInt(part2);
      
      // If first part > 12, it must be DD/MM/YYYY
      if (num1 > 12) {
        return new Date(parseInt(year), num2 - 1, num1);
      }
      // If second part > 12, it must be MM/DD/YYYY
      if (num2 > 12) {
        return new Date(parseInt(year), num1 - 1, num2);
      }
      // Ambiguous case: try DD/MM/YYYY first (more common in India)
      // Check if the date makes sense as DD/MM
      const dateAsDDMM = new Date(parseInt(year), num2 - 1, num1);
      if (dateAsDDMM.getDate() === num1 && dateAsDDMM.getMonth() === num2 - 1) {
        return dateAsDDMM; // DD/MM/YYYY
      }
      // Otherwise try MM/DD/YYYY
      return new Date(parseInt(year), num1 - 1, num2);
    }
    
    // Try other formats with dashes
    const dashMatch = dateOnly.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (dashMatch) {
      const [, part1, part2, year] = dashMatch;
      const num1 = parseInt(part1);
      const num2 = parseInt(part2);
      
      if (num1 > 12) {
        return new Date(parseInt(year), num2 - 1, num1); // DD-MM-YYYY
      }
      if (num2 > 12) {
        return new Date(parseInt(year), num1 - 1, num2); // MM-DD-YYYY
      }
      // Try DD-MM-YYYY first
      const dateAsDDMM = new Date(parseInt(year), num2 - 1, num1);
      if (dateAsDDMM.getDate() === num1 && dateAsDDMM.getMonth() === num2 - 1) {
        return dateAsDDMM;
      }
      return new Date(parseInt(year), num1 - 1, num2);
    }
    
    // Fallback to Date constructor
    const parsed = new Date(dateOnly);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  // Normalize date to ISO format (YYYY-MM-DD) for grouping
  const normalizeDateToISO = (dateStr: string): string => {
    if (!dateStr) return '';
    
    const date = parseDateString(dateStr);
    if (!date) {
      console.warn('Failed to parse date:', dateStr);
      // If it's already in ISO format, return it
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      return dateStr; // Return original if parsing fails
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date for display
  const formatDateForDisplay = (dateStr: string): string => {
    const date = parseDateString(dateStr);
    if (!date) return dateStr;
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Group income logs by date and calculate daily summaries
  const getGroupedLogsByDate = () => {
    const grouped: { [date: string]: { logs: IncomeLog[]; totalKg: number; totalMoney: number; displayDate: string } } = {};
    
    incomeLogs.slice(0, 100).forEach((log) => {
      // Normalize date to ISO format for grouping - use sale_date, not created_at
      const normalizedDate = normalizeDateToISO(log.sale_date);
      
      if (!normalizedDate) {
        // Skip logs with invalid dates
        console.warn('Skipping log with invalid sale_date:', log.sale_date, log.id);
        return;
      }
      
      if (!grouped[normalizedDate]) {
        // Use the first log's sale_date for display date
        grouped[normalizedDate] = { 
          logs: [], 
          totalKg: 0, 
          totalMoney: 0, 
          displayDate: formatDateForDisplay(log.sale_date) 
        };
      }
      
      grouped[normalizedDate].logs.push(log);
      grouped[normalizedDate].totalKg += log.quantity_sold_kg || 0;
      grouped[normalizedDate].totalMoney += log.amount_earned || 0;
    });
    
    // Debug: log grouped dates
    if (Object.keys(grouped).length > 0) {
      console.log('Grouped dates:', Object.keys(grouped).sort().reverse());
    }
    
    // Convert to array and sort by date (newest first)
    return Object.entries(grouped)
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => b.date.localeCompare(a.date));
  };

  // Get available quantity from species inventory
  const getAvailableQuantity = (): number | null => {
    if (!watchedPondId || speciesInventory.length === 0) {
      return null;
    }
    
    // If species is selected, use that species' biomass
    if (watchedSpecies) {
      const selectedSpecies = speciesInventory.find(s => s.species === watchedSpecies);
      return selectedSpecies ? selectedSpecies.biomass_kg : null;
    }
    
    // Otherwise, use the species with most biomass (or first one)
    if (speciesInventory.length > 0) {
      return speciesInventory.reduce((max, species) => 
        species.biomass_kg > max ? species.biomass_kg : max, 0
      );
    }
    
    return null;
  };

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">
          Go to Location Selection
        </Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/data-entry" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">💵 Income Log</h1>
          <p className="text-gray-600">{selectedLocation.name}</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Summary Cards */}
      {incomeLogs.length > 0 && (
        <div className="grid grid-cols-2 gap-4">
          <div className="card p-4">
            <div className="text-center">
              <div className="text-xs text-gray-500 mb-1">Total Income</div>
              <div className="text-lg font-bold text-green-600">
                ₹{getTotalIncome().toLocaleString()}
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="text-center">
              <div className="text-xs text-gray-500 mb-1">Total Sold</div>
              <div className="text-lg font-bold text-blue-600">
                {getTotalQuantitySold().toFixed(1)} kg
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Income Entry Form */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <PlusIcon className="h-5 w-5 mr-2" />
          Record Sale/Income
        </h2>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* 1. Sale Date * */}
            <div>
              <label className="form-label">Sale Date *</label>
              <input
                type="date"
                {...form.register('sale_date')}
                className="form-input"
              />
              {form.formState.errors.sale_date && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.sale_date.message}</p>
              )}
            </div>

            {businessType === 'fish' ? (
              <div>
                <label className="form-label">Pond *</label>
                <select {...form.register('pond_id')} className="form-input">
                  <option value="">Select Pond</option>
                  {ponds?.map((pond) => (
                    <option key={pond.id} value={pond.id}>
                      {pond.name} - {pond.pond_type === 'nursery' ? 'Nursery' : 'Grower'}
                    </option>
                  ))}
                </select>
                {form.formState.errors.pond_id && (
                  <p className="mt-1 text-sm text-red-600">{form.formState.errors.pond_id.message}</p>
                )}
              </div>
            ) : (
              <>
                {/* 2. Goats Being Sold (Optional) */}
                <div>
                  <label className="form-label">Goats Being Sold (Optional)</label>
                  {loadingGoats ? (
                    <div className="form-input bg-gray-50 flex items-center">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2 text-sm text-gray-500">Loading goats...</span>
                    </div>
                  ) : (
                    <select
                      className="form-input"
                      value={watchedGoatId}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        form.setValue('goat_ids', selectedId ? [selectedId] : [], { shouldValidate: true });
                      }}
                    >
                      <option value="">Select Goat</option>
                      {goats.length > 0 ? (
                        goats.map((goat) => (
                          <option key={goat.id} value={goat.id}>
                            {goat.tag_no} - {goat.category === 'doe' ? 'Doe' : goat.category === 'buck' ? 'Buck' : 'Kid'} {goat.breed ? `(${goat.breed})` : ''}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>No active goats found</option>
                      )}
                    </select>
                  )}
                  {form.formState.errors.goat_ids && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.goat_ids.message}</p>
                  )}
                </div>

                {/* 3. Shed (Optional) */}
                <div>
                  <label className="form-label">Shed (Optional)</label>
                  <select {...form.register('shed_id')} className="form-input">
                    <option value="">General (Not shed-specific)</option>
                    {sheds.map((shed) => (
                      <option key={shed.id} value={shed.id}>
                        {shed.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 4. Goat weight when sold */}
                <div>
                  <label className="form-label">Goat weight when sold</label>
                  <input
                    type="number"
                    step="0.1"
                    {...form.register('quantity_sold_kg')}
                    className="form-input"
                    placeholder="0.0"
                  />
                  {form.formState.errors.quantity_sold_kg && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.quantity_sold_kg.message}</p>
                  )}
                </div>
              </>
            )}

            {businessType === 'fish' && watchedPondId && (
              <div className="sm:col-span-2">
                <label className="form-label">Fish Species</label>
                {loadingAllSpecies ? (
                  <div className="form-input bg-gray-50 flex items-center">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2 text-sm text-gray-500">Loading species...</span>
                  </div>
                ) : allFishSpecies.length > 0 ? (
                  <>
                    <select {...form.register('fish_species')} className="form-input">
                      <option value="">Select Species (optional)</option>
                      {allFishSpecies.map((species) => {
                        // Find matching species in inventory if available
                        const inventorySpecies = speciesInventory.find(
                          s => s.species.toLowerCase() === species.name.toLowerCase()
                        );
                        const biomass = inventorySpecies ? inventorySpecies.biomass_kg : 0;
                        const count = inventorySpecies ? inventorySpecies.count : 0;
                        return (
                          <option key={species.id} value={species.name}>
                            {species.name} ({biomass.toFixed(2)} kg available, {count} fish)
                          </option>
                        );
                      })}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      All species are shown. Select the species being sold.
                    </p>
                  </>
                ) : (
                  <>
                    <select {...form.register('fish_species')} className="form-input" disabled>
                      <option value="">No species found</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      No fish species available in master data.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Amount Earned and Quantity Sold (for fish) */}
            {businessType === 'fish' && (
              <>
                <div>
                  <label className="form-label">Amount Earned (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    {...form.register('amount_earned')}
                    className="form-input"
                    placeholder="0.00"
                  />
                  {form.formState.errors.amount_earned && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.amount_earned.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Quantity Sold (kg) *</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    {...form.register('quantity_sold_kg')}
                    className="form-input"
                    placeholder="0.0"
                  />
                  {(() => {
                    const availableQty = getAvailableQuantity();
                    if (watchedPondId && availableQty !== null) {
                      return (
                        <p className="mt-1 text-xs text-gray-500">
                          Available in inventory: <span className="font-semibold">{availableQty.toFixed(2)} kg</span> (informational only)
                        </p>
                      );
                    }
                    if (watchedPondId && loadingSpecies) {
                      return (
                        <p className="mt-1 text-xs text-gray-500">Loading inventory...</p>
                      );
                    }
                    return null;
                  })()}
                  {form.formState.errors.quantity_sold_kg && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.quantity_sold_kg.message}</p>
                  )}
                </div>
              </>
            )}

            {/* Amount Earned (for goat) */}
            {businessType === 'goat' && (
              <div>
                <label className="form-label">Amount Earned (₹) *</label>
                <input
                  type="number"
                  step="0.01"
                  {...form.register('amount_earned')}
                  className="form-input"
                  placeholder="0.00"
                />
                {form.formState.errors.amount_earned && (
                  <p className="mt-1 text-sm text-red-600">{form.formState.errors.amount_earned.message}</p>
                )}
              </div>
            )}

            {calculatePricePerKg() && (
              <div>
                <label className="form-label">Price per kg (₹)</label>
                <input
                  type="text"
                  value={`₹${calculatePricePerKg()}/kg`}
                  className="form-input bg-gray-50"
                  disabled
                />
                <p className="mt-1 text-xs text-gray-500">Auto-calculated from amount ÷ quantity</p>
              </div>
            )}

            {businessType === 'fish' ? (
              <>
                <div>
                  <label className="form-label">Fish Count</label>
                  <input
                    type="number"
                    {...form.register('fish_count')}
                    className="form-input"
                    placeholder="0"
                  />
                  <p className="mt-1 text-xs text-gray-500">Total number of fish sold</p>
                </div>

                {calculateAvgWeight() && (
                  <div>
                    <label className="form-label">Avg Weight (g)</label>
                    <input
                      type="text"
                      value={`${calculateAvgWeight()} g`}
                      className="form-input bg-gray-50"
                      disabled
                    />
                    <p className="mt-1 text-xs text-gray-500">Auto-calculated from weight ÷ count</p>
                  </div>
                )}
              </>
            ) : null}

            <div>
              <label className="form-label">Buyer Name</label>
              <input
                type="text"
                {...form.register('buyer_name')}
                className="form-input"
                placeholder="Customer/buyer name"
              />
            </div>

            <div>
              <label className="form-label">Buyer Contact</label>
              <input
                type="text"
                {...form.register('buyer_contact')}
                className="form-input"
                placeholder="Phone number or contact"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="form-label">Remarks</label>
              <textarea
                {...form.register('remarks')}
                className="form-input"
                rows={2}
                placeholder="Fish quality, market conditions, payment method, etc."
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={creating}
            className="w-full btn-touch bg-emerald-600 text-white py-3 rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center"
          >
            {creating ? <LoadingSpinner size="sm" /> : 'Record Sale/Income'}
          </button>
        </form>
      </div>

      {/* Recent Income Logs */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Sales</h2>
        
        {loading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner />
          </div>
        ) : incomeLogs.length > 0 ? (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {getGroupedLogsByDate().map((dayGroup) => (
              <div key={dayGroup.date} className="space-y-2">
                {/* Daily Summary Strip */}
                <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-lg p-3 sticky top-0 z-10">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <span className="font-semibold text-gray-700">
                        {dayGroup.displayDate}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="text-xs text-gray-600">Total Sold</div>
                        <div className="font-bold text-emerald-700">{dayGroup.totalKg.toFixed(1)} kg</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-600">Total Income</div>
                        <div className="font-bold text-green-700">₹{dayGroup.totalMoney.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Individual Sales for this day */}
                {dayGroup.logs.map((log) => (
                  <div key={log.id} className="border border-gray-200 rounded-lg p-4 ml-2">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="font-bold text-green-600">₹{log.amount_earned.toLocaleString()}</span>
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-800 text-xs rounded-full">
                            {log.quantity_sold_kg} kg
                          </span>
                          {log.fish_count && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              {log.fish_count} fish
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div>Pond: {getPondName(log.pond_id)}</div>
                          <div>Price: ₹{(log.amount_earned / log.quantity_sold_kg).toFixed(2)}/kg</div>
                          {log.avg_weight_g && <div>Avg Weight: {log.avg_weight_g}g</div>}
                          {log.buyer_name && <div>Buyer: {log.buyer_name}</div>}
                          {log.buyer_contact && <div>Contact: {log.buyer_contact}</div>}
                          {log.remarks && <div>Notes: {log.remarks}</div>}
                        </div>
                      </div>
                      <div className="flex flex-col items-end space-y-2">
                        <div className="text-xs text-gray-500">
                          {formatDateForDisplay(log.sale_date)}
                        </div>
                        {isAdmin && (
                          <button
                            onClick={() => handleEdit(log)}
                            className="btn-touch p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                            title="Edit sale"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">💵</div>
            <p className="text-gray-500">No sales records yet</p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Edit Sale</h2>
              <button
                onClick={handleCancelEdit}
                className="btn-touch p-2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Sale Date *</label>
                  <input
                    type="date"
                    value={editFormData.sale_date || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, sale_date: e.target.value })}
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="form-label">Pond *</label>
                  <select
                    value={editFormData.pond_id || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, pond_id: e.target.value })}
                    className="form-input"
                  >
                    <option value="">Select Pond</option>
                    {ponds?.map((pond) => (
                      <option key={pond.id} value={pond.id}>
                        {pond.name} - {pond.pond_type === 'nursery' ? 'Nursery' : 'Grower'}
                      </option>
                    ))}
                  </select>
                </div>

                {editFormData.pond_id && (
                  <div className="sm:col-span-2">
                    <label className="form-label">Fish Species</label>
                    {loadingAllSpecies ? (
                      <div className="form-input bg-gray-50 flex items-center">
                        <LoadingSpinner size="sm" />
                        <span className="ml-2 text-sm text-gray-500">Loading species...</span>
                      </div>
                    ) : allFishSpecies.length > 0 ? (
                      <select
                        value={editFormData.fish_species || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, fish_species: e.target.value || undefined })}
                        className="form-input"
                      >
                        <option value="">Select Species (optional)</option>
                        {allFishSpecies.map((species) => (
                          <option key={species.id} value={species.name}>
                            {species.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select className="form-input" disabled>
                        <option value="">No species found</option>
                      </select>
                    )}
                  </div>
                )}

                <div>
                  <label className="form-label">Amount Earned (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editFormData.amount_earned || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, amount_earned: parseFloat(e.target.value) || 0 })}
                    className="form-input"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="form-label">Quantity Sold (kg)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editFormData.quantity_sold_kg || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, quantity_sold_kg: parseFloat(e.target.value) || undefined })}
                    className="form-input"
                    placeholder="0.0"
                  />
                </div>

                <div>
                  <label className="form-label">Fish Count</label>
                  <input
                    type="number"
                    value={editFormData.fish_count || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, fish_count: parseInt(e.target.value) || undefined })}
                    className="form-input"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="form-label">Buyer Name</label>
                  <input
                    type="text"
                    value={editFormData.buyer_name || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, buyer_name: e.target.value || undefined })}
                    className="form-input"
                    placeholder="Customer/buyer name"
                  />
                </div>

                <div>
                  <label className="form-label">Buyer Contact</label>
                  <input
                    type="text"
                    value={editFormData.buyer_contact || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, buyer_contact: e.target.value || undefined })}
                    className="form-input"
                    placeholder="Phone number or contact"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="form-label">Remarks</label>
                  <textarea
                    value={editFormData.remarks || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, remarks: e.target.value || undefined })}
                    className="form-input"
                    rows={2}
                    placeholder="Fish quality, market conditions, payment method, etc."
                  />
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleUpdateSubmit}
                  disabled={updating || !editFormData.sale_date || !editFormData.pond_id || !editFormData.amount_earned}
                  className="flex-1 btn-touch bg-emerald-600 text-white py-3 rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center"
                >
                  {updating ? <LoadingSpinner size="sm" /> : 'Update Sale'}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={updating}
                  className="flex-1 btn-touch bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomeForm;